import { useState, useEffect } from 'react';
import { MembershipElementContentModel } from '@/services/platform/api/model';
import {
  getMembershipAutoRenewal,
  requestMembershipUpgrade,
  setMembershipAutoRenewal,
} from '@/services/platform/data/profile';
import { cn, randomUuid, useClientTranslation, ModuleProperties } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { ProfileMembership } from '@/components/shared/profile/membership';
import { ConfirmationSheet } from '@/components/shared/sheets/confirmation';
import { InformativeSheet } from '@/components/shared/sheets/informative';
import { SpinLoading } from '@/components/shared/loadings/spin';

export const Membership = (props: ModuleProperties) => {
  const content = props.content as MembershipElementContentModel | undefined;
  const p = content?.properties;
  const setHideNavbar = useUIStore(state => state.setHideNavbar);
  const t = useClientTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [autoRenewal, setAutoRenewal] = useState(false);
  const [canEnableAutoRenewal, setCanEnableAutoRenewal] = useState(true);
  const [renewalBlockedSheetOpen, setRenewalBlockedSheetOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);

  useEffect(() => {
    async function load() {
      const autoRenewalState = await getMembershipAutoRenewal();
      setAutoRenewal(autoRenewalState?.enabled ?? false);
      setCanEnableAutoRenewal(autoRenewalState?.canEnable ?? false);
      setIsLoading(false);
    }
    load();
  }, []);

  async function handleAutoRenewalChange(enabled: boolean) {
    const previous = autoRenewal;
    setAutoRenewal(enabled);

    const result = await setMembershipAutoRenewal({ enabled });
    if (!result) {
      setAutoRenewal(previous);
      return;
    }

    setAutoRenewal(result.enabled ?? enabled);
    setCanEnableAutoRenewal(result.canEnable ?? canEnableAutoRenewal);
  }

  if (!content) return null;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['module__membership']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      {isLoading ? (
        <SpinLoading wrapperClassName="min-h-40" />
      ) : (
        <ProfileMembership
          autoRenewal={autoRenewal}
          onAutoRenewalChange={handleAutoRenewalChange}
          renewalLocked={!canEnableAutoRenewal}
          onRenewalLockedAttempt={() => setRenewalBlockedSheetOpen(true)}
          onRequestUpgrade={() => setUpgradeModalOpen(true)}
          renewalSectionTitle={p?.membershipRenewalTitle}
          renewalToggleLabel={p?.allowAutomaticLabel}
          renewalToggleDescription={p?.allowAutomaticDescription}
          upgradeSectionTitle={p?.membershipUpgradeTitle}
          upgradeDescription={p?.membershipUpgradeDescription}
          upgradeButtonLabel={p?.membershipUpgradeBtnLabel}
        />
      )}

      <InformativeSheet
        open={renewalBlockedSheetOpen}
        onOpenChange={setRenewalBlockedSheetOpen}
        title={t('App.Labels.MembershipRenewalBlockedTitle')}
        descriptionText={t('App.Labels.MembershipRenewalBlockedDescription')}
        cancelLabel={t('App.Labels.Close')}
      />

      <ConfirmationSheet
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
        headerTitle={p?.membershipUpgradeModalMasterTitle}
        title={p?.membershipUpgradeModalTitle}
        description={p?.membershipUpgradeModalDescription}
        confirmLabel={p?.membershipUpgradeModalUpgradeBtnLabel}
        dismissLabel={p?.membershipUpgradeModalCancelBtnLabel}
        successText={p?.membershipUpgradeModalConfirmation}
        onConfirm={async () => {
          const result = await requestMembershipUpgrade(randomUuid());
          if (!result) {
            throw new Error('requestMembershipUpgrade failed');
          }
        }}
      />
    </ModuleWrapper>
  );
};
